
import React from "react";
import { useNavigate } from "react-router-dom";
import useModuleProgress from "../hooks/useModuleProgress";
import "../styles/pages/_module-pages.scss";
import "../styles/pages/_learn-assessments.scss";
import "../styles/pages/_quiz-completion.scss";

interface ModuleSection {
  title: string;
  content: string[];
  additionalSection?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: "Why Relationships Matter",
    content: [
      "<strong>Learning Goal:</strong> Understand the value of positive relationships at work and how they support teamwork and well-being.",
      "<br/>",
      "<strong>Key Topics:</strong>",
      "• Importance of professional relationships",
      "• Benefits for teamwork and feeling valued",
      "• Common challenges",
      "<br/>",
      "<strong>Discussion:</strong> Positive relationships at work create a supportive environment, improve teamwork, and help you feel valued."
    ]
  },
  {
    title: "Ways to Build Relationships",
    content: [
      "Practical ways to build relationships:",
      "<br/>",
      "• Communicate openly and respectfully",
      "• Offer help and appreciate others' efforts",
      "• Participate in team activities and meetings",
      "• Resolve conflicts calmly and constructively",
      "<br/>",
      "<strong>Mini Practice:</strong> Compliment a colleague or offer help today."
    ]
  },
  {
    title: "Reflection",
    content: [
      "Think about how you interact with your colleagues:",
      "• What can you do to strengthen your professional relationships?",
      "<br/>",
      "<strong>Journal Prompt:</strong> 'Today, I built a better relationship at work by…'"
    ]
  },
  {
    title: "Ready to Test Your Knowledge?",
    content: [
      "You've completed Module 3: Building Professional Relationships! Now, let's reinforce your learning with a quick quiz."
    ],
    additionalSection: {
      title: "Next Steps",
      content: [
        "Click 'Take the Quiz' below to test your knowledge about building professional relationships.",
        "The quiz will help you review key concepts about workplace relationships."
      ]
    }
  }
];

const OccupationalModule3Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress("occupationalM3");
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
  navigate("/panel/occupational-module-3/quiz");
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate("/panel/occupational-resources");
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left occupational-panel"
        >
          <div className="module-header">
            <h1>Module 3</h1>
            <p className="module-subtitle">Building Professional Relationships</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Understand the value of positive relationships at work and how they support teamwork and well-being.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? "Exit Module" : "Previous"}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate("/panel/occupational-module-3/quiz")}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationalModule3Page;


